// Global error handler to catch errors from third-party scripts
window.addEventListener('error', function(event) {
  // Suppress errors from third-party scripts that we can't control
  if (event.filename && (
    event.filename.includes('shop_events_listener') ||
    event.filename.includes('birdchime') ||
    event.filename.includes('signature-egg-tarts') ||
    event.filename.includes('b98688ed7w4720d202p17656315m70f274dfm') ||
    event.filename.includes('abeef7a0') ||
    event.message.includes('Cannot read properties of undefined')
  )) {
    console.warn('Third-party script error suppressed:', event.message, 'from', event.filename);
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
}, true);

// Also catch unhandled promise rejections
window.addEventListener('unhandledrejection', function(event) {
  if (event.reason && (
    event.reason.message && event.reason.message.includes('Cannot read properties of undefined') ||
    event.reason.stack && event.reason.stack.includes('shop_events_listener')
  )) {
    console.warn('Unhandled promise rejection suppressed:', event.reason);
    event.preventDefault();
  }
});

// Fetch the store's default-locale product title for printing. This is a
// same-storefront request: no token, app proxy, or external server is used.
// If the lookup cannot complete, keep the current storefront title exactly as
// requested (for this store that is normally the Traditional Chinese title).
window.BHPrintEnglishTitle = window.BHPrintEnglishTitle || (() => {
  const titleCache = new Map();
  const requestTimeoutMs = 2500;

  return async function getEnglishProductTitle(handle, fallbackTitle = '') {
    const safeHandle = String(handle || '').trim();
    const fallback = String(fallbackTitle || '').trim();
    if (!safeHandle) return fallback;

    if (!titleCache.has(safeHandle)) {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), requestTimeoutMs);
      const titleRequest = fetch(`/products/${encodeURIComponent(safeHandle)}.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Product lookup failed: ${response.status}`);
          return response.json();
        })
        .then((productData) => String(productData?.title || fallback).trim())
        .catch((error) => {
          console.warn('[Print English title] Using current storefront title:', error);
          titleCache.delete(safeHandle);
          return fallback;
        })
        .finally(() => window.clearTimeout(timeoutId));

      titleCache.set(safeHandle, titleRequest);
    }

    return titleCache.get(safeHandle);
  };
})();

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.printTitlePromise = this.ensureEnglishPrintTitle();
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        // Add timeout to prevent button from being stuck forever
        const timeoutId = setTimeout(() => {
          console.warn('Add to cart timeout - resetting button');
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          const spinner = this.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
        }, 10000); // 10 second timeout

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        await this.ensureEnglishPrintTitle();

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // Debug logging
        console.log('Adding to cart:', {
          url: routes.cart_add_url,
          variantId: formData.get('id'),
          quantity: formData.get('quantity')
        });

        // Validate cart URL
        if (!routes.cart_add_url) {
          console.error('Cart add URL is not defined');
          throw new Error('Cart URL is not configured. Please refresh the page.');
        }

        // Wrap fetch in try-catch to prevent third-party script interference
        // Use native fetch if available, otherwise fallback to XMLHttpRequest
        let fetchPromise;
        try {
          // Check if fetch is available and not overridden
          if (typeof fetch === 'function') {
            fetchPromise = fetch(`${routes.cart_add_url}`, config);
          } else {
            throw new Error('Fetch API not available');
          }
        } catch (fetchError) {
          console.error('Fetch initialization error:', fetchError);
          throw new Error('Failed to initialize cart request. Please try again.');
        }

        fetchPromise
          .then((response) => {
            console.log('Cart add response:', response);
            
            // Additional validation to prevent third-party script errors
            if (!response || typeof response !== 'object') {
              console.error('Invalid response object:', response);
              console.error('Fetch may be intercepted. Trying XMLHttpRequest fallback...');
              
              // Fallback to XMLHttpRequest
              return this.fallbackCartAdd(formData);
            }
            
            if (!response.ok) {
              console.error('Response not OK:', response.status, response.statusText);
              throw new Error(`Failed to add to cart: ${response.status} ${response.statusText}`);
            }
            
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error('Add to cart error:', e);
            
            // Determine error type and show appropriate message
            let errorMessage = 'Unable to add item to cart. Please try again.';
            
            if (e.message.includes('Failed to fetch') || e.message.includes('NetworkError')) {
              errorMessage = 'Network error. Please check your internet connection and try again.';
            } else if (e.message.includes('No response from server')) {
              errorMessage = 'Server is not responding. Please try again in a moment.';
            } else if (e.message.includes('status')) {
              errorMessage = `Server error (${e.message.match(/\d{3}/)?.[0] || 'unknown'}). Please try again.`;
            }
            
            // Show user-friendly error message
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: errorMessage,
              message: e.message
            });
            this.error = true;
          })
          .finally(() => {
            // Clear the timeout since we're done
            clearTimeout(timeoutId);
            
            // ALWAYS reset button state, even if there was an error
            try {
              this.submitButton.classList.remove('loading');
              if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              const spinner = this.querySelector('.loading__spinner');
              if (spinner) spinner.classList.add('hidden');
              CartPerformance.measureFromEvent("add:user-action", evt);
            } catch (cleanupError) {
              console.error('Error during cleanup:', cleanupError);
            }
          });
      }

      async ensureEnglishPrintTitle() {
        const englishTitle = await window.BHPrintEnglishTitle(
          this.dataset.productHandle,
          this.dataset.productTitleCurrent
        );

        if (!englishTitle) return '';

        let titleInput = this.form.querySelector('input[name="properties[_print_title_en]"]');
        if (!titleInput) {
          titleInput = document.createElement('input');
          titleInput.type = 'hidden';
          titleInput.name = 'properties[_print_title_en]';
          this.form.appendChild(titleInput);
        }
        titleInput.value = englishTitle;
        return englishTitle;
      }

      // Fallback method using XMLHttpRequest when fetch is blocked
      fallbackCartAdd(formData) {
        console.log('[Fallback] Using XMLHttpRequest for cart add');
        
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', routes.cart_add_url);
          xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
          
          xhr.onload = function() {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const response = JSON.parse(xhr.responseText);
                resolve(response);
              } catch (e) {
                reject(new Error('Failed to parse cart response'));
              }
            } else {
              reject(new Error(`Cart add failed: ${xhr.status} ${xhr.statusText}`));
            }
          };
          
          xhr.onerror = function() {
            reject(new Error('Network error during cart add'));
          };
          
          xhr.send(formData);
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
